import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Plus, Clock } from 'lucide-react';
import { useTask } from '@/contexts/TaskContext';
import { saveTasks } from '@/utils/storage';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/components/ui/use-toast';

export function QuickAddTask() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [duration, setDuration] = useState('25');
  const [scheduledTime, setScheduledTime] = useState('');
  const { refreshTasks } = useTask();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!taskTitle.trim()) {
      toast({
        title: "Task title required",
        description: "Please enter a task title",
        variant: "destructive",
      });
      return;
    }

    try {
      const allTasks = await import('@/utils/storage').then(m => m.loadTasks());
      const newTask = {
        id: uuidv4(),
        title: taskTitle.trim(),
        duration: parseInt(duration) || 25,
        scheduledTime: scheduledTime || new Date().toLocaleTimeString('en-US', { 
          hour12: false, 
          hour: '2-digit', 
          minute: '2-digit' 
        }),
        completed: false,
        reminderSet: false,
        completionSound: true,
        createdAt: new Date().toISOString(),
        isActive: false,
        isPaused: false,
      };

      await saveTasks([...allTasks, newTask]);
      await refreshTasks();
      
      // Reset form
      setTaskTitle('');
      setDuration('25');
      setScheduledTime('');
      setIsExpanded(false);

      toast({
        title: "Task added",
        description: `"${newTask.title}" has been added to your tasks`,
      });

      // Dispatch event for other components
      window.dispatchEvent(new CustomEvent('tasks-updated', { 
        detail: { type: 'task-added', task: newTask }
      }));
    } catch (error) {
      console.error('Error adding task:', error);
      toast({
        title: "Error",
        description: "Failed to add task. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-2 border-primary/20 shadow-lg">
      <div className="p-4">
        {!isExpanded ? (
          <Button
            onClick={() => setIsExpanded(true)}
            className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3 px-6 rounded-lg shadow-md flex items-center justify-center gap-2"
            size="lg"
          >
            <Plus className="h-5 w-5" />
            Quick Add Task
          </Button>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Plus className="h-5 w-5 text-primary" />
              <h3 className="font-semibold text-primary">Add New Task</h3>
            </div>
            
            <div>
              <Input
                placeholder="What do you need to get done?"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                className="text-base"
                autoFocus
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Duration (minutes)
                </label>
                <Input
                  type="number"
                  placeholder="25"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  min="1"
                  max="240"
                />
              </div>
              
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">
                  Start time (optional)
                </label>
                <Input
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                <Clock className="h-4 w-4 mr-2" />
                Add Task
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsExpanded(false);
                  setTaskTitle('');
                  setDuration('25');
                  setScheduledTime('');
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}